// Inline edit overlay for the public site.
// Loads on every page; only activates when the bm_admin cookie is present.
// Scans for [data-edit] elements (e.g., data-edit="dj:jym-equalizer"), shows
// a hover affordance, and opens a modal containing the same form the full
// /admin/ UI uses (imported from ./forms.ts).
import type { Content, Dj, EventItem, PagesContent, Release, Post } from "../types.ts";
import {
  el,
  renderDjForm, createNewDj,
  renderEventForm, createNewEvent,
  renderHomeForm,
  renderPageForm,
  renderReleaseForm, createNewRelease,
  renderPostForm, createNewPost,
} from "./forms.ts";

type PageKey = keyof PagesContent;

function isLoggedIn(): boolean {
  return /(?:^|;\s*)bm_admin=([^;]+)/.test(document.cookie || "");
}

type EditTarget =
  | { kind: "dj"; slug: string }
  | { kind: "event"; slug: string }
  | { kind: "release"; slug: string }
  | { kind: "post"; slug: string }
  | { kind: "home" }
  | { kind: "page"; pageKey: PageKey };

const VALID_PAGE_KEYS = new Set<PageKey>(["about", "contact", "demos", "releases", "not_found"]);

function parseEditAttr(value: string | null): EditTarget | null {
  if (!value) return null;
  const [kind, rest] = value.split(":", 2);
  if (kind === "dj" && rest) return { kind: "dj", slug: rest };
  if (kind === "event" && rest) return { kind: "event", slug: rest };
  if (kind === "release" && rest) return { kind: "release", slug: rest };
  if (kind === "post" && rest) return { kind: "post", slug: rest };
  if (kind === "home") return { kind: "home" };
  if (kind === "page" && rest && VALID_PAGE_KEYS.has(rest as PageKey)) {
    return { kind: "page", pageKey: rest as PageKey };
  }
  return null;
}

type AddTarget =
  | { kind: "dj" }
  | { kind: "event" }
  | { kind: "release" }
  | { kind: "post" };

function parseAddAttr(value: string | null): AddTarget | null {
  if (value === "dj") return { kind: "dj" };
  if (value === "event") return { kind: "event" };
  if (value === "release") return { kind: "release" };
  if (value === "post") return { kind: "post" };
  return null;
}

const STYLES = `
[data-edit] {
  position: relative;
  outline-offset: 2px;
  transition: outline-color 0.15s ease;
}
[data-edit]:not(.ie-active):hover {
  outline: 2px dashed rgba(230, 196, 90, 0.7);
  cursor: pointer;
}
[data-edit] .ie-pill {
  position: absolute;
  top: 6px; right: 6px;
  background: rgba(230, 196, 90, 0.95);
  color: #1a1500;
  border: none;
  padding: 4px 10px;
  border-radius: 14px;
  font: 600 11px/1 system-ui, sans-serif;
  cursor: pointer;
  z-index: 5;
  opacity: 0;
  transform: translateY(-3px);
  transition: opacity 0.15s ease, transform 0.15s ease;
  pointer-events: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}
[data-edit]:hover .ie-pill {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

/* Add-record button — persistent (no hover gating). */
.ie-add-pill {
  display: inline-flex; align-items: center; gap: 6px;
  background: rgba(230, 196, 90, 0.95);
  color: #1a1500;
  border: none;
  padding: 8px 14px;
  border-radius: 18px;
  font: 600 12px/1 system-ui, sans-serif;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  margin: 16px 0;
  z-index: 6;
}
.ie-add-pill:hover { filter: brightness(1.05); }
.ie-add-bar {
  display: flex; justify-content: flex-end;
  padding: 0 16px;
}

.ie-back {
  position: fixed; inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 10000;
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
}
.ie-modal {
  background: #131722;
  color: #e9eef7;
  border: 1px solid #2a3142;
  border-radius: 8px;
  width: min(720px, 100%); height: min(86vh, 800px);
  display: grid; grid-template-rows: auto 1fr auto;
  font: 14px/1.5 system-ui, sans-serif;
  overflow: hidden;
}
.ie-modal > header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; border-bottom: 1px solid #2a3142;
}
.ie-modal > header .title { font-weight: 650; }
.ie-modal > .body { overflow: auto; padding: 16px 18px; }
.ie-modal > footer {
  padding: 12px 16px; border-top: 1px solid #2a3142;
  display: flex; gap: 10px; justify-content: flex-end; align-items: center;
}
.ie-modal .ie-status { font: 12px/1 ui-monospace, monospace; color: #97a5b6; margin-right: auto; }
.ie-modal .ie-status.is-busy { color: #e6c45a; }
.ie-modal .ie-status.is-err { color: #ff6a6a; }
.ie-modal .ie-status.is-ok { color: #6fd29d; }

/* Reuse a minimal subset of admin form styling. */
.ie-modal .field { margin: 0 0 14px; }
.ie-modal .field > label { display: block; font-weight: 600; margin: 0 0 4px; font-size: 13px; }
.ie-modal .field > .help { color: #97a5b6; font-size: 12px; margin: 0 0 5px; }
.ie-modal .field input[type="text"],
.ie-modal .field input[type="url"],
.ie-modal .field input[type="email"],
.ie-modal .field input[type="password"],
.ie-modal .field textarea,
.ie-modal .field select {
  width: 100%; padding: 8px 10px;
  background: #0b0d12; border: 1px solid #2a3142; color: #e9eef7;
  border-radius: 5px; font: inherit;
}
.ie-modal .field textarea { font-family: ui-monospace, monospace; font-size: 12.5px; min-height: 80px; resize: vertical; }
.ie-modal .field input:focus, .ie-modal .field textarea:focus, .ie-modal .field select:focus { outline: none; border-color: #e6c45a; }
.ie-modal .row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.ie-modal .fieldset { border: 1px solid #2a3142; border-radius: 6px; padding: 14px; margin: 0 0 18px; background: #0b0d12; }
.ie-modal .fieldset > legend { padding: 0 6px; font-weight: 600; color: #e6c45a; }
.ie-modal .tag-list { display: flex; flex-wrap: wrap; gap: 6px; padding: 6px 0; }
.ie-modal .tag-list .tag { padding: 3px 8px; background: #1a2030; border: 1px solid #2a3142; border-radius: 12px; font-size: 12px; display: flex; align-items: center; gap: 4px; }
.ie-modal .tag-list .tag .x { cursor: pointer; color: #97a5b6; }
.ie-modal .tag-list .tag .x:hover { color: #ff6a6a; }
.ie-modal .btn {
  padding: 7px 14px; border: 1px solid #2a3142; background: #1a2030; color: #e9eef7;
  border-radius: 6px; cursor: pointer; font: inherit;
}
.ie-modal .btn:hover { border-color: #97a5b6; }
.ie-modal .btn:disabled { opacity: 0.5; cursor: not-allowed; }
.ie-modal .btn.primary { background: #e6c45a; color: #1a1500; border-color: transparent; font-weight: 600; }
.ie-modal .btn.ghost { background: transparent; }
.ie-modal .btn.danger { color: #ff6a6a; }
.ie-modal h3 { margin: 0 0 12px; }

/* Image picker control (Browse + thumb) inside form fields. */
.ie-modal .imgctl { display: flex; gap: 10px; align-items: stretch; }
.ie-modal .imgctl .thumb {
  width: 84px; height: 56px; flex-shrink: 0; border-radius: 4px; border: 1px solid #2a3142;
  background: #0b0d12 center/cover no-repeat; cursor: pointer;
  display: flex; align-items: center; justify-content: center; color: #97a5b6; font-size: 11px;
}
.ie-modal .imgctl .thumb:hover { border-color: #e6c45a; }
.ie-modal .imgctl .right { flex: 1; display: flex; flex-direction: column; gap: 4px; }
.ie-modal .imgctl .row { display: flex; gap: 6px; }
.ie-modal .imgctl input[type="text"] { flex: 1; }
.ie-modal .imgctl .clear {
  background: transparent; border: 1px solid #2a3142; color: #97a5b6;
  border-radius: 5px; cursor: pointer; padding: 0 8px;
}
.ie-modal .imgctl .clear:hover { color: #ff6a6a; }

/* Image picker modal (mounted on document.body, not inside .ie-modal) — needs
   higher z-index than the inline modal back, and full layout since admin/style.css
   isn't loaded on the public site. */
.imgpicker-back {
  position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 10001;
  display: flex; align-items: center; justify-content: center;
}
.imgpicker {
  background: #131722; color: #e9eef7; border: 1px solid #2a3142; border-radius: 8px;
  width: min(92vw, 1000px); height: min(86vh, 720px);
  display: grid; grid-template-rows: auto 1fr auto; overflow: hidden;
  font: 14px/1.5 system-ui, sans-serif;
}
.imgpicker > header {
  display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-bottom: 1px solid #2a3142;
}
.imgpicker > header .title { font-weight: 600; }
.imgpicker > header .spacer { flex: 1; }
.imgpicker .grid {
  overflow: auto; padding: 12px;
  display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px;
}
.imgpicker .tile {
  position: relative; border: 1px solid #2a3142; border-radius: 5px; background: #0b0d12;
  overflow: hidden; cursor: pointer;
}
.imgpicker .tile:hover { border-color: #e6c45a; }
.imgpicker .tile img { width: 100%; height: 110px; object-fit: cover; display: block; }
.imgpicker .tile .name { padding: 4px 6px; font-size: 11px; font-family: ui-monospace, monospace; color: #97a5b6; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.imgpicker .tile .del {
  position: absolute; top: 4px; right: 4px;
  background: rgba(0,0,0,0.7); color: #ff6a6a; border: none;
  width: 22px; height: 22px; border-radius: 4px; cursor: pointer; font-size: 14px; line-height: 1;
  opacity: 0; transition: opacity 0.15s;
}
.imgpicker .tile:hover .del { opacity: 1; }
.imgpicker .tile .del:hover { background: #ff6a6a; color: white; }
.imgpicker > footer {
  padding: 10px 16px; border-top: 1px solid #2a3142;
  display: flex; gap: 10px; align-items: center;
}
.imgpicker > footer label { font-size: 12px; color: #97a5b6; }
.imgpicker > footer input[type="text"], .imgpicker > footer select {
  padding: 6px 8px; background: #0b0d12; border: 1px solid #2a3142; color: #e9eef7;
  border-radius: 4px; font: inherit;
}
.imgpicker > footer input[type="file"] { color: #e9eef7; font: inherit; }
.imgpicker > footer .btn {
  padding: 7px 14px; border: 1px solid #2a3142; background: #1a2030; color: #e9eef7;
  border-radius: 6px; cursor: pointer; font: inherit;
}
.imgpicker > footer .btn:hover { border-color: #97a5b6; }
.imgpicker > footer .btn.primary { background: #e6c45a; color: #1a1500; border-color: transparent; font-weight: 600; }
.imgpicker > footer .btn.ghost { background: transparent; }
.imgpicker .empty { padding: 40px; text-align: center; color: #97a5b6; grid-column: 1/-1; }
.imgpicker .upstatus { color: #e6c45a; font-size: 12px; }
`;

function injectStyles(): void {
  if (document.getElementById("ie-styles")) return;
  const style = document.createElement("style");
  style.id = "ie-styles";
  style.textContent = STYLES;
  document.head.appendChild(style);
}

async function fetchContent(): Promise<Content> {
  const r = await fetch("/admin/api/content", { credentials: "same-origin" });
  if (!r.ok) throw new Error(`fetch content failed: ${r.status}`);
  return await r.json() as Content;
}

async function savePut(section: "roster" | "events" | "home" | "pages" | "releases_list" | "posts", payload: unknown): Promise<void> {
  const r = await fetch(`/admin/api/${section}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(payload),
  });
  if (!r.ok) throw new Error(`save failed: ${r.status} ${await r.text()}`);
}

// ---- Generic modal scaffolding ----
//
// Each opener: build modal frame, fetch content, render form into body, wire
// Save button. Keeps the per-section flow short.

interface ModalFrame {
  back: HTMLElement;
  body: HTMLElement;
  status: HTMLElement;
  saveBtn: HTMLButtonElement;
  cancelBtn: HTMLButtonElement;
  setStatus: (msg: string, kind?: "" | "busy" | "ok" | "err") => void;
  close: (force?: boolean) => void;
}

function createModalFrame(title: string, saveLabel: string, opts: { dirtyOnOpen?: boolean }): ModalFrame {
  const back = el("div", { class: "ie-back", role: "dialog", "aria-modal": "true" });
  const modal = el("div", { class: "ie-modal" });
  const header = el("header", {});
  const body = el("div", { class: "body" });
  const footer = el("footer", {});

  let dirty = !!opts.dirtyOnOpen;
  const status = el("span", { class: "ie-status" }, "loading…");
  status.classList.add("is-busy");

  function setStatus(msg: string, kind: "" | "busy" | "ok" | "err" = ""): void {
    status.textContent = msg;
    status.className = "ie-status" + (kind ? " is-" + kind : "");
  }

  function close(force: boolean = false): void {
    if (!force && dirty && !opts.dirtyOnOpen && !confirm("You have unsaved changes. Discard and close?")) return;
    if (back.parentNode) back.parentNode.removeChild(back);
    document.removeEventListener("keydown", escHandler);
  }
  function escHandler(e: KeyboardEvent): void { if (e.key === "Escape") close(); }
  back.addEventListener("click", e => { if (e.target === back) close(); });
  document.addEventListener("keydown", escHandler);

  header.appendChild(el("div", { class: "title" }, title));
  header.appendChild(el("button", { type: "button", class: "btn ghost", onClick: () => close() }, "Close"));

  const cancelBtn = el("button", { type: "button", class: "btn ghost", onClick: () => close() }, "Cancel") as HTMLButtonElement;
  const saveBtn = el("button", { type: "button", class: "btn primary" }, saveLabel) as HTMLButtonElement;
  saveBtn.disabled = !dirty;
  footer.appendChild(status);
  footer.appendChild(cancelBtn);
  footer.appendChild(saveBtn);

  modal.appendChild(header);
  modal.appendChild(body);
  modal.appendChild(footer);
  back.appendChild(modal);
  document.body.appendChild(back);

  // Hook the form's onChange into the dirty flag below — the openers set this
  // by reassigning `dirty = true` from inside the form's onChange callback,
  // routed via setStatus("unsaved changes", "busy").
  const obs = new Proxy({}, {});
  void obs;

  return {
    back, body, status, saveBtn, cancelBtn, setStatus, close,
    // intentionally do not expose `dirty` — callers manage their own flag
  };
}

function openDjModal(slug: string, mode: "edit" | "create" = "edit"): void {
  const back = el("div", { class: "ie-back", role: "dialog", "aria-modal": "true" });
  const modal = el("div", { class: "ie-modal" });
  const header = el("header", {});
  const body = el("div", { class: "body" });
  const footer = el("footer", {});

  // In create mode the new DJ doesn't exist on the server until the user hits
  // Save, so closing without saving should always discard cleanly. In edit
  // mode we warn before discarding unsaved changes.
  let dirty = mode === "create";
  const status = el("span", { class: "ie-status" }, "loading…");
  status.classList.add("is-busy");

  function setStatus(msg: string, kind: "" | "busy" | "ok" | "err" = ""): void {
    status.textContent = msg;
    status.className = "ie-status" + (kind ? " is-" + kind : "");
  }

  function close(force: boolean = false): void {
    if (!force && mode === "edit" && dirty && !confirm("You have unsaved changes. Discard and close?")) return;
    document.body.removeChild(back);
    document.removeEventListener("keydown", escHandler);
  }
  function escHandler(e: KeyboardEvent): void { if (e.key === "Escape") close(); }
  back.addEventListener("click", e => { if (e.target === back) close(); });
  document.addEventListener("keydown", escHandler);

  header.appendChild(el("div", { class: "title" }, mode === "create" ? `Add DJ · ${slug}` : `Edit · ${slug}`));
  header.appendChild(el("button", { type: "button", class: "btn ghost", onClick: () => close() }, "Close"));

  const cancelBtn = el("button", { type: "button", class: "btn ghost", onClick: () => close() }, "Cancel");
  const saveLabel = mode === "create" ? "Create & open" : "Save & reload";
  const saveBtn = el("button", { type: "button", class: "btn primary" }, saveLabel) as HTMLButtonElement;
  saveBtn.disabled = mode === "edit"; // create mode: enabled immediately so user can save with defaults if they want
  footer.appendChild(status);
  footer.appendChild(cancelBtn);
  footer.appendChild(saveBtn);

  modal.appendChild(header);
  modal.appendChild(body);
  modal.appendChild(footer);
  back.appendChild(modal);
  document.body.appendChild(back);

  void (async () => {
    try {
      const content = await fetchContent();
      let dj: Dj;
      if (mode === "create") {
        if (content.roster.djs.some(d => d.slug === slug)) {
          body.innerHTML = `<p>A DJ with slug <code>${slug}</code> already exists. Pick a different slug.</p>`;
          setStatus("conflict", "err");
          saveBtn.disabled = true;
          return;
        }
        dj = createNewDj(slug);
        content.roster.djs.push(dj);
      } else {
        const found = content.roster.djs.find(d => d.slug === slug);
        if (!found) {
          body.innerHTML = `<p>No DJ found with slug <code>${slug}</code>. The roster may have changed since this page was rendered — reload the page to refresh.</p>`;
          setStatus("not found", "err");
          return;
        }
        dj = found;
      }

      setStatus(mode === "create" ? "ready · fill in details and save" : "");
      renderDjForm(dj, body, {
        heading: "",
        showDelete: false,
        onChange: () => {
          dirty = true;
          saveBtn.disabled = false;
          setStatus("unsaved changes", "busy");
        },
      });
      saveBtn.addEventListener("click", async () => {
        saveBtn.disabled = true;
        cancelBtn.disabled = true;
        setStatus("saving…", "busy");
        try {
          await savePut("roster", content.roster);
          setStatus("saved · reloading…", "ok");
          if (mode === "create") {
            // Open the freshly-created profile page so the user can keep editing.
            window.location.assign(`/roster/${dj.slug}/`);
          } else {
            // If slug changed during edit, navigate to the new URL.
            const onProfilePage = /^\/roster\/[^/]+\/?$/.test(window.location.pathname);
            if (onProfilePage && dj.slug !== slug) {
              window.location.assign(`/roster/${dj.slug}/`);
            } else {
              window.location.reload();
            }
          }
        } catch (err) {
          setStatus(`save failed: ${String((err as Error).message ?? err)}`, "err");
          saveBtn.disabled = false;
          cancelBtn.disabled = false;
        }
      });
    } catch (err) {
      body.innerHTML = `<p>Failed to load: ${String((err as Error).message ?? err)}</p>`;
      setStatus("error", "err");
    }
  })();
}

function promptForNewDjSlug(): string | null {
  return promptForSlug("Slug for new DJ (lowercase, hyphens — used as the URL: /roster/<slug>/):");
}

function promptForNewEventSlug(): string | null {
  return promptForSlug("Slug for new event (lowercase, hyphens — used as the URL: /events/<slug>/):");
}

function promptForSlug(prompttext: string): string | null {
  const raw = prompt(prompttext);
  if (raw === null) return null;
  const slug = raw.trim().toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "");
  if (!slug) {
    alert("Slug cannot be empty.");
    return null;
  }
  return slug;
}

// ---- Event modal ----

function openEventModal(slug: string, mode: "edit" | "create" = "edit"): void {
  const frame = createModalFrame(
    mode === "create" ? `Add event · ${slug}` : `Edit event · ${slug}`,
    mode === "create" ? "Create & open" : "Save & reload",
    { dirtyOnOpen: mode === "create" },
  );

  void (async () => {
    try {
      const content = await fetchContent();
      let ev: EventItem;
      if (mode === "create") {
        if (content.events.events.some(e => e.slug === slug)) {
          frame.body.innerHTML = `<p>An event with slug <code>${slug}</code> already exists.</p>`;
          frame.setStatus("conflict", "err");
          frame.saveBtn.disabled = true;
          return;
        }
        ev = createNewEvent(slug);
        content.events.events.push(ev);
      } else {
        const found = content.events.events.find(e => e.slug === slug);
        if (!found) {
          frame.body.innerHTML = `<p>No event found with slug <code>${slug}</code>. Reload the page to refresh.</p>`;
          frame.setStatus("not found", "err");
          return;
        }
        ev = found;
      }

      frame.setStatus(mode === "create" ? "ready · fill in details and save" : "");
      renderEventForm(ev, frame.body, {
        heading: "",
        showDelete: false,
        onChange: () => {
          frame.saveBtn.disabled = false;
          frame.setStatus("unsaved changes", "busy");
        },
      });
      frame.saveBtn.addEventListener("click", async () => {
        frame.saveBtn.disabled = true;
        frame.cancelBtn.disabled = true;
        frame.setStatus("saving…", "busy");
        try {
          await savePut("events", content.events);
          frame.setStatus("saved · reloading…", "ok");
          if (mode === "create") {
            window.location.assign(`/events/${ev.slug}/`);
          } else {
            const onEventPage = /^\/events\/[^/]+\/?$/.test(window.location.pathname);
            if (onEventPage && ev.slug !== slug) {
              window.location.assign(`/events/${ev.slug}/`);
            } else {
              window.location.reload();
            }
          }
        } catch (err) {
          frame.setStatus(`save failed: ${String((err as Error).message ?? err)}`, "err");
          frame.saveBtn.disabled = false;
          frame.cancelBtn.disabled = false;
        }
      });
    } catch (err) {
      frame.body.innerHTML = `<p>Failed to load: ${String((err as Error).message ?? err)}</p>`;
      frame.setStatus("error", "err");
    }
  })();
}

// ---- Release modal ----

function openReleaseModal(slug: string, mode: "edit" | "create" = "edit"): void {
  const frame = createModalFrame(
    mode === "create" ? `Add release · ${slug}` : `Edit release · ${slug}`,
    mode === "create" ? "Create & open" : "Save & reload",
    { dirtyOnOpen: mode === "create" },
  );

  void (async () => {
    try {
      const content = await fetchContent();
      let rel: Release;
      if (mode === "create") {
        if (content.releases_list.releases.some(r => r.slug === slug)) {
          frame.body.innerHTML = `<p>A release with slug <code>${slug}</code> already exists.</p>`;
          frame.setStatus("conflict", "err");
          frame.saveBtn.disabled = true;
          return;
        }
        rel = createNewRelease(slug);
        content.releases_list.releases.push(rel);
      } else {
        const found = content.releases_list.releases.find(r => r.slug === slug);
        if (!found) {
          frame.body.innerHTML = `<p>No release found with slug <code>${slug}</code>. Reload the page to refresh.</p>`;
          frame.setStatus("not found", "err");
          return;
        }
        rel = found;
      }

      frame.setStatus(mode === "create" ? "ready · fill in details and save" : "");
      renderReleaseForm(rel, frame.body, {
        heading: "",
        showDelete: false,
        onChange: () => {
          frame.saveBtn.disabled = false;
          frame.setStatus("unsaved changes", "busy");
        },
      });
      frame.saveBtn.addEventListener("click", async () => {
        frame.saveBtn.disabled = true;
        frame.cancelBtn.disabled = true;
        frame.setStatus("saving…", "busy");
        try {
          await savePut("releases_list", content.releases_list);
          frame.setStatus("saved · reloading…", "ok");
          if (mode === "create") {
            window.location.assign(`/releases/${rel.slug}/`);
          } else {
            const onReleasePage = /^\/releases\/[^/]+\/?$/.test(window.location.pathname);
            if (onReleasePage && rel.slug !== slug) {
              window.location.assign(`/releases/${rel.slug}/`);
            } else {
              window.location.reload();
            }
          }
        } catch (err) {
          frame.setStatus(`save failed: ${String((err as Error).message ?? err)}`, "err");
          frame.saveBtn.disabled = false;
          frame.cancelBtn.disabled = false;
        }
      });
    } catch (err) {
      frame.body.innerHTML = `<p>Failed to load: ${String((err as Error).message ?? err)}</p>`;
      frame.setStatus("error", "err");
    }
  })();
}

// ---- Post modal ----

function openPostModal(slug: string, mode: "edit" | "create" = "edit"): void {
  const frame = createModalFrame(
    mode === "create" ? `Add post · ${slug}` : `Edit post · ${slug}`,
    mode === "create" ? "Create & open" : "Save & reload",
    { dirtyOnOpen: mode === "create" },
  );

  void (async () => {
    try {
      const content = await fetchContent();
      let post: Post;
      if (mode === "create") {
        if (content.posts.posts.some(p => p.slug === slug)) {
          frame.body.innerHTML = `<p>A post with slug <code>${slug}</code> already exists.</p>`;
          frame.setStatus("conflict", "err");
          frame.saveBtn.disabled = true;
          return;
        }
        post = createNewPost(slug);
        content.posts.posts.push(post);
      } else {
        const found = content.posts.posts.find(p => p.slug === slug);
        if (!found) {
          frame.body.innerHTML = `<p>No post found with slug <code>${slug}</code>. Reload the page to refresh.</p>`;
          frame.setStatus("not found", "err");
          return;
        }
        post = found;
      }

      frame.setStatus(mode === "create" ? "ready · fill in details and save" : "");
      renderPostForm(post, frame.body, {
        heading: "",
        showDelete: false,
        onChange: () => {
          frame.saveBtn.disabled = false;
          frame.setStatus("unsaved changes", "busy");
        },
      });
      frame.saveBtn.addEventListener("click", async () => {
        frame.saveBtn.disabled = true;
        frame.cancelBtn.disabled = true;
        frame.setStatus("saving…", "busy");
        try {
          await savePut("posts", content.posts);
          frame.setStatus("saved · reloading…", "ok");
          if (mode === "create") {
            // If the post is a draft (no published_at), redirecting to /posts/<slug>/
            // would 404; land on /posts/ instead so the editor sees the result of save.
            if (!post.published_at) {
              window.location.assign("/posts/");
            } else {
              window.location.assign(`/posts/${post.slug}/`);
            }
          } else {
            const onPostPage = /^\/posts\/[^/]+\/?$/.test(window.location.pathname);
            if (onPostPage && post.slug !== slug) {
              window.location.assign(`/posts/${post.slug}/`);
            } else {
              window.location.reload();
            }
          }
        } catch (err) {
          frame.setStatus(`save failed: ${String((err as Error).message ?? err)}`, "err");
          frame.saveBtn.disabled = false;
          frame.cancelBtn.disabled = false;
        }
      });
    } catch (err) {
      frame.body.innerHTML = `<p>Failed to load: ${String((err as Error).message ?? err)}</p>`;
      frame.setStatus("error", "err");
    }
  })();
}

// ---- Home modal ----

function openHomeModal(): void {
  const frame = createModalFrame("Edit home page", "Save & reload", { dirtyOnOpen: false });

  void (async () => {
    try {
      const content = await fetchContent();
      frame.setStatus("");
      renderHomeForm(content.home, frame.body, {
        heading: "",
        onChange: () => {
          frame.saveBtn.disabled = false;
          frame.setStatus("unsaved changes", "busy");
        },
      });
      frame.saveBtn.addEventListener("click", async () => {
        frame.saveBtn.disabled = true;
        frame.cancelBtn.disabled = true;
        frame.setStatus("saving…", "busy");
        try {
          await savePut("home", content.home);
          frame.setStatus("saved · reloading…", "ok");
          window.location.reload();
        } catch (err) {
          frame.setStatus(`save failed: ${String((err as Error).message ?? err)}`, "err");
          frame.saveBtn.disabled = false;
          frame.cancelBtn.disabled = false;
        }
      });
    } catch (err) {
      frame.body.innerHTML = `<p>Failed to load: ${String((err as Error).message ?? err)}</p>`;
      frame.setStatus("error", "err");
    }
  })();
}

// ---- Static page modal ----

function openPageModal(pageKey: PageKey): void {
  const label = pageKey === "not_found" ? "404 page" : pageKey;
  const frame = createModalFrame(`Edit ${label}`, "Save & reload", { dirtyOnOpen: false });

  void (async () => {
    try {
      const content = await fetchContent();
      frame.setStatus("");
      renderPageForm(pageKey, content.pages, frame.body, {
        heading: "",
        onChange: () => {
          frame.saveBtn.disabled = false;
          frame.setStatus("unsaved changes", "busy");
        },
      });
      frame.saveBtn.addEventListener("click", async () => {
        frame.saveBtn.disabled = true;
        frame.cancelBtn.disabled = true;
        frame.setStatus("saving…", "busy");
        try {
          await savePut("pages", content.pages);
          frame.setStatus("saved · reloading…", "ok");
          window.location.reload();
        } catch (err) {
          frame.setStatus(`save failed: ${String((err as Error).message ?? err)}`, "err");
          frame.saveBtn.disabled = false;
          frame.cancelBtn.disabled = false;
        }
      });
    } catch (err) {
      frame.body.innerHTML = `<p>Failed to load: ${String((err as Error).message ?? err)}</p>`;
      frame.setStatus("error", "err");
    }
  })();
}

function decorate(): void {
  document.querySelectorAll<HTMLElement>("[data-edit]").forEach(node => {
    if (node.dataset.ieDecorated) return;
    node.dataset.ieDecorated = "1";

    const target = parseEditAttr(node.getAttribute("data-edit"));
    if (!target) return;

    const pill = document.createElement("button");
    pill.type = "button";
    pill.className = "ie-pill";
    pill.textContent = "Edit";
    pill.title = target.kind === "page"
      ? `Edit ${target.pageKey}`
      : target.kind === "home"
        ? "Edit home page"
        : `Edit ${target.kind}: ${target.slug}`;
    pill.addEventListener("click", e => {
      e.preventDefault();
      e.stopPropagation();
      if (target.kind === "dj") openDjModal(target.slug, "edit");
      else if (target.kind === "event") openEventModal(target.slug, "edit");
      else if (target.kind === "release") openReleaseModal(target.slug, "edit");
      else if (target.kind === "post") openPostModal(target.slug, "edit");
      else if (target.kind === "home") openHomeModal();
      else if (target.kind === "page") openPageModal(target.pageKey);
    });

    const cs = window.getComputedStyle(node);
    if (cs.position === "static") node.style.position = "relative";
    node.appendChild(pill);
  });

  document.querySelectorAll<HTMLElement>("[data-add]").forEach(node => {
    if (node.dataset.ieAddDecorated) return;
    node.dataset.ieAddDecorated = "1";

    const target = parseAddAttr(node.getAttribute("data-add"));
    if (!target) return;

    const bar = document.createElement("div");
    bar.className = "ie-add-bar";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "ie-add-pill";
    btn.textContent =
      target.kind === "dj"      ? "+ Add DJ"
    : target.kind === "event"   ? "+ Add event"
    : target.kind === "release" ? "+ Add release"
    : target.kind === "post"    ? "+ Add post"
    : "+ Add";
    btn.addEventListener("click", e => {
      e.preventDefault();
      e.stopPropagation();
      if (target.kind === "dj") {
        const slug = promptForNewDjSlug();
        if (slug) openDjModal(slug, "create");
      } else if (target.kind === "event") {
        const slug = promptForNewEventSlug();
        if (slug) openEventModal(slug, "create");
      } else if (target.kind === "release") {
        const slug = promptForSlug("Slug for new release (lowercase, hyphens — used as the URL: /releases/<slug>/):");
        if (slug) openReleaseModal(slug, "create");
      } else if (target.kind === "post") {
        const slug = promptForSlug("Slug for new post (lowercase, hyphens — used as the URL: /posts/<slug>/):");
        if (slug) openPostModal(slug, "create");
      }
    });
    bar.appendChild(btn);
    node.insertBefore(bar, node.firstChild);
  });
}

function init(): void {
  if (!isLoggedIn()) return;
  injectStyles();
  decorate();
  // Re-decorate after site.js rewrites the DOM (it moves body children into a
  // .maincol wrapper). Use a MutationObserver to catch the shuffle.
  const mo = new MutationObserver(() => decorate());
  mo.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
